import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatMoney } from "@parthub/shared";
import { reasonPromptFor, useReasonDialog } from "../components/ReasonDialog";
import { statusLabel, useT } from "../i18n";

function normalizeRows(payload) {
  return Array.isArray(payload?.data) ? payload.data : [];
}

function normalizePagination(payload) {
  const meta = payload?.meta || {};
  return {
    page: Number(meta.page || 1),
    total: Number(meta.total || 0),
    totalPages: Number(meta.total_pages || 1),
  };
}

function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-50 text-green-700 border-green-200";
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "rejected":
      return "bg-red-50 text-red-700 border-red-200";
    case "archived":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

export default function PartsPage() {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const status = searchParams.get("status") || "pending";

  async function loadRows(nextStatus = status) {
    try {
      setLoading(true);
      setError("");
      const res = await api.get(`/api/v1/admin/parts?status=${encodeURIComponent(nextStatus)}`);
      setRows(normalizeRows(res));
      setMeta(normalizePagination(res));
    } catch (err) {
      setError(err?.message || t("Failed to load parts"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, [status]);

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();

  async function updateStatus(id, nextStatus, label) {
    // Negative decisions ask for a message the seller will see.
    const prompt = reasonPromptFor("part", nextStatus, label);
    let reason;
    if (prompt) {
      reason = await askReason(prompt);
      if (reason === null) return;
    }
    try {
      await api.patch(`/api/v1/admin/parts/${id}/status`, { status: nextStatus, reason });
      await loadRows();
    } catch (err) {
      setError(err?.message || t("Failed to update part status"));
    }
  }

  function setStatusFilter(nextStatus) {
    const qs = new URLSearchParams(searchParams);
    qs.set("status", nextStatus);
    setSearchParams(qs, { replace: true });
  }

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-700 p-6 text-white shadow-lg sm:p-8">
        <h1 className="text-3xl font-bold sm:text-5xl">{t("Part moderation")}</h1>
        <p className="mt-3 text-sm text-indigo-100 sm:text-base">
          {t("Review marketplace listings, approve high-quality parts, and reject poor or invalid submissions.")}
        </p>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {["pending", "active", "rejected", "archived"].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setStatusFilter(item)}
              className={[
                "inline-flex h-10 items-center justify-center rounded-2xl border px-4 text-sm font-semibold transition",
                status === item
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          {loading ? t("Loading parts...") : (meta.total === 1 ? t("1 listing") : t("{n} listings", { n: meta.total }))}
        </div>
      </div>

      {error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-5 text-red-700 shadow-sm">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">{t("Loading moderation queue...")}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">{t("No parts found for this status.")}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {rows.map((part) => (
              <div key={part.id} className="p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold text-gray-900">{part.title}</h2>
                      <span
                        className={[
                          "rounded-full border px-3 py-1 text-xs font-semibold",
                          statusClasses(part.status),
                        ].join(" ")}
                      >
                        {statusLabel(part.status)}
                      </span>
                    </div>

                    <p className="mt-1 text-sm text-gray-500">
                      {t("seller: {seller}", { seller: part.seller_name || t("unknown") })} • {t("price: {price}", { price: formatMoney(part.price) })} • slug: {part.slug}
                    </p>

                    <p className="mt-2 text-sm text-gray-600">
                      {part.description || t("No part description available.")}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/parts/${part.id}`}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-800 transition hover:bg-gray-50"
                    >
                      {t("View details")}
                    </Link>

                    <button
                      type="button"
                      onClick={() => updateStatus(part.id, "active", part.title)}
                      className="inline-flex h-10 items-center justify-center rounded-2xl bg-green-600 px-4 text-sm font-semibold text-white transition hover:bg-green-700"
                    >
                      {t("Approve")}
                    </button>

                    <button
                      type="button"
                      onClick={() => updateStatus(part.id, "rejected", part.title)}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                    >
                      {t("Reject")}
                    </button>

                    <button
                      type="button"
                      onClick={() => updateStatus(part.id, "archived", part.title)}
                      className="inline-flex h-10 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
                    >
                      {t("Archive")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {reasonDialog}
    </section>
  );
}
